import { config } from '@abyss/web/tools/config';
import { storage } from '@abyss/web/tools/storage';
import { Button } from '@abyss/web/ui/Button';
import { Layout } from '@abyss/web/ui/Layout';
import { Modal } from '@abyss/web/ui/Modal';
import find from 'lodash/find';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useSaveEducationalContentStatus } from '../../hooks/useEducationalContent';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useHsidUuid } from '../../hooks/useHsidUuid';
import { useLagoon } from '../../hooks/useLagoon';
import { StoreKeys } from '../../hooks/useStore/state';
import { useStore } from '../../hooks/useStore/useStore';
import { adobeLinkTrackEvent } from '../AdobeTagging/adobeLinkTrackEvent';
import { adobeModalTrackEvent } from '../AdobeTagging/adobeModalTrackEvent';
import { Constants } from '../Constants';
import { ConstantsLagoon } from '../ConstantsLagoon';
import {
  StyledModal,
  StyledModelSection,
  StyledSubTitle,
  StyledTitle,
} from './EducateCompareModalPopup.styled';

export const EducateCompareModalPopup = ({
  onPressTryItNow = () => {},
  sectionType,
}) => {
  const lagoonData = useLagoon('ui-messaging')();
  const compare_providers_title_description = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING
      .COMPARE_PROVIDERS_MODAL_POPUP_TITLE_AND_DESCRIPTION,
  });
  const [showMergedCompareAndShareFF, isNewComparePopupFlowEnabled] =
    useFeatureFlag([
      ConstantsLagoon?.FEATURE_FLAGS?.ENABLE_COMPARE_SMART_CHOICE,
      ConstantsLagoon.FEATURE_FLAGS.ENABLE_NEW_COMPARE_POPOVER_FLOW,
    ]);

  const showMergedCompareAndShare =
    showMergedCompareAndShareFF &&
    sectionType === Constants.RESULT_SECTION.PROVIDER;

  const { t } = useTranslation();

  const comparePopuImage = `${config(
    'CDN_BASE_URL'
  )}/cdn/assets/images/smartChoiceCompare.png`;

  const educationalContentStore = useStore(
    StoreKeys.EDUCATIONAL_CONTENT_STATUS
  );
  const setEducationalContentStore = useStore(
    StoreKeys.SET_EDUCATIONAL_CONTENT_STATUS
  );
  const compareCoachmarkState = (bool) =>
    isNewComparePopupFlowEnabled && bool
      ? { showCompareCoachmarkWeb: false }
      : {};

  const [getEducationalContentStatus, setEducationalContentStatus] =
    useSaveEducationalContentStatus({});
  const hsidUuid = useHsidUuid();
  const isSuperUser =
    storage.session.get(Constants.STORAGE_KEYS.SESSION.SUPER_USER) || false;
  const closeEducateCompareModalPopup = async (bool) => {
    await setEducationalContentStatus({
      variables: {
        hasViewedContent: educationalContentStore?.hasViewedContent,
        showCompareModal: false,
        ...compareCoachmarkState(bool),
        ...(hsidUuid && { userId: hsidUuid }),
        isSuperUser,
      },
    });
    if (bool) {
      onPressTryItNow();
    }
    if (isNewComparePopupFlowEnabled && !bool) {
      sessionStorage.setItem(
        Constants.STORAGE_KEYS.SESSION.HAS_DISMISSED_COMPARE_COACHMARK_WEB,
        'y'
      );
    }
  };
  useEffect(() => {
    if (getEducationalContentStatus?.data?.saveEducationalContentStatus) {
      const { saveEducationalContentStatus } =
        getEducationalContentStatus?.data;
      setEducationalContentStore({
        ...educationalContentStore,
        hasViewedContent: saveEducationalContentStatus?.hasViewedContent,
        showCompareModal: saveEducationalContentStatus?.showCompareModal,
        showCompareCoachmarkWeb:
          saveEducationalContentStatus?.showCompareCoachmarkWeb === null
            ? educationalContentStore?.showCompareCoachmarkWeb
            : saveEducationalContentStatus?.showCompareCoachmarkWeb,
      });
    }
  }, [getEducationalContentStatus]);

  useEffect(() => {
    if (educationalContentStore?.showCompareModal) {
      adobeModalTrackEvent('Compare providers');
    }
  }, [educationalContentStore?.showCompareModal]);
  const adobeTrackingForEducateCompare = (linkName: string) => {
    adobeLinkTrackEvent({
      name: linkName,
      location: 'Compare Providers',
      type: 'Internal',
    });
  };

  return (
    <React.Fragment>
      {showMergedCompareAndShare && (
        <StyledModal
          closeOnClickOutside={false}
          data-testid="compare-model"
          footer={
            <Modal.Section>
              <Layout.Group alignLayout="right">
                <Button
                  data-testid="close-modal-may-be-later"
                  onClick={() => {
                    adobeTrackingForEducateCompare('May be later');
                    closeEducateCompareModalPopup(false);
                  }}
                  variant="outline"
                >
                  {t('COMPARE_MODAL.MAY_BE_LATER')}
                </Button>
                <Button
                  data-testid="close-modal-try-it-now"
                  onClick={() => {
                    adobeTrackingForEducateCompare('try it now');
                    closeEducateCompareModalPopup(true);
                  }}
                >
                  {t('COMPARE_MODAL.TRY_IT_NOW')}
                </Button>
              </Layout.Group>
            </Modal.Section>
          }
          isOpen={educationalContentStore?.showCompareModal}
          model="closeOnClickOutside-modal"
          onClose={() => {
            adobeTrackingForEducateCompare('close icon');
            closeEducateCompareModalPopup(false);
          }}
          size={572}
          title={
            <StyledTitle>
              {compare_providers_title_description?.title}
            </StyledTitle>
          }
        >
          <Modal.Section>
            <StyledSubTitle>
              {compare_providers_title_description?.message}
            </StyledSubTitle>
          </Modal.Section>
          <StyledModelSection>
            <img alt="comparepopup" src={comparePopuImage} />
          </StyledModelSection>
        </StyledModal>
      )}
    </React.Fragment>
  );
};
